import {ReactElement, useContext, useEffect, useMemo, useState} from "react";
import * as css from "./styles/css.module.scss";
import {SERIES_DATA_OPTION, usePropertyExecutiveSummaryData} from "./logic/usePropertyExecutiveSummaryData";
import {Property} from "../../contexts/properties/PropertiesContext";
import {OperationalGraph} from "../planning-dashboard/components/operational-graph/OperationalGraph";
import GraphTableWidget from "../planning-dashboard/components/graph-table-widget/GraphTableWidget";
import MenuTitle from "../../components/menu-title/MenuTitle";
import * as widgetCss from "./styles/widget.module.scss";
import * as statCSS from "../../components/account-summary/styles/styles.module.scss";
import {Col, Grid, Row} from "@zendeskgarden/react-grid";
import WidgetLoading from "../planning-dashboard/components/widget-loading/WidgetLoading";
import {ThemeProvider} from "styled-components";
import {ViziblyTheme} from "../../styles/zendesk-garden/ViziblyZDGTheme";
import OperationalTable from "../planning-dashboard/components/operational-table/OperationalTable";
import {ExecutiveSummaryGraphData} from "./logic/seriesGraphDataLogic";
import useAppStore from "../../hooks/useAppStore";
import {FinancialReportTable} from "./components/FinancialSummaryTable";
import {DownloadButton} from "./pdf/DownloadButton";
import {
    buildExecutiveSummaryStatementNOI,
    buildRentsStatementMarketRent,
    buildRentsStatementMoveOutRatio,
    buildRentsStatementOccupancy,
    buildRentsStatementRenewalRatio,
    buildRentsStatementResidentsTurnover,
    buildRentsStatementSummary,
    buildRentsStatementTradeouts,
    formatAsJSX
} from "./logic/summaryStringBuilders";
import {ASSET_LIVING_WORKSPACE_IDS, CUSTOM_T12_TEXT, IClientBrandingAssetBuilt, loadClientBrandAsset, OPERATIONAL_MENU_OPTIONS} from "./constants";
import {TabPanel, Tabs} from "@zendeskgarden/react-tabs";
import {Button, ToggleButton} from "@zendeskgarden/react-buttons";
import cn from "classnames";
import MetricBlock from "./components/MetricBlock";
import brand from "../../assets/icons/brand/brand.png";
import {AddRounded, Favorite, ShareRounded} from "@material-ui/icons";
import {ShareModal} from "./components/ShareModal";
import {RevenueSummaryTable} from "../workflows/operational/summary/RevenueSummaryTable";
import FinancialsCOATable from "./components/FinancialsCOATable";
import {formatterDollarUSNoDecimal, formatterPercentAutoSign, formatterPercentWithSign} from "../../utils/formatters";
import {EmployeeTable} from "./components/EmployeeTable";
import {
    ClientFeaturesModel,
    useGetBrandingInformationQuery,
    useGetBudgetApprovalStatusLazyQuery,
    useGetExecutiveSummaryNarrativeQuery,
    useGetMyPropertiesLazyQuery,
    useListPropertyBudgetYearsLazyQuery,
    useSetBudgetApprovalMutation,
} from "../../__generated__/generated_types";
import {Body, Close, Header, Modal} from "@zendeskgarden/react-modals";
import FlappyViz from "../../fun/flappy-viz/FlappyViz";
import {Dropdown, Field, Item, Menu, Select} from "@zendeskgarden/react-dropdowns";
import {AuthUser} from "../../contexts/auth_types";
import {mapProperty, propertyDisplayBudgetYear} from "../../contexts/properties/logic";
import {Inline} from "@zendeskgarden/react-loaders";
import {COLORS} from "../../constants/Colors";
import {ReactComponent as InfoIcon} from '@zendeskgarden/svg-icons/src/16/info-stroke.svg';
import {Tooltip} from "@zendeskgarden/react-tooltips";
import "native-injects";
import {getMonths} from "../../utils/months-helper";
import ExecutiveSummaryBanner from "./components/ExecutiveSummaryBanner";
import {AuthContext} from "../../contexts/AuthContext";
import {Authorizations} from "../../authorization/Authorizations";
import {checkAuthorization} from "../../authorization/AuthorizationCheckers";
import {isExecutiveSummaryAccessRestricted, showExecutiveSummaryCustomNarrative} from "../../global/feature_checkers";
import {useHistory, useRouteMatch} from "react-router-dom";
import {ReportsNavMenuConfig} from "../../components/navbar/components/reports-nav-menu/ReportsNavMenuConfig";
import ExecutiveSummaryMetricBlock from "./components/ExecutiveSummaryMetricBlock";
import NarrativeEditor from "./narrative-editor/NarrativeEditor";

export enum ExecutiveSummaryTabs {
    summary = "summary",
    financials = "financials",
    revenue = "revenue",
    payroll = "payroll",
}

export type ExecutiveSummaryUserInfo = {
    clientId: string,
    clientName: string,
    clientDisplayName: string,
    isGuest: boolean,
    isAdmin: boolean,
    rootClientId?: string | null,
    clientFeatures: ClientFeaturesModel,
};

export interface IPropertyExecutiveSummaryProps {
    property: Property,
    user: ExecutiveSummaryUserInfo;
}

interface IPropertyBrandedExecutiveSummaryProps extends IPropertyExecutiveSummaryProps {
    branding: IClientBrandingAssetBuilt;
}

function BrandedPropertyExecutiveSummary(props: IPropertyBrandedExecutiveSummaryProps): ReactElement {
    const customNarrativeFeatureEnabled = showExecutiveSummaryCustomNarrative(props.user);
    const [budgetYear, setBudgetYear] = useState(props.property.budgetYear);
    const [budgetYears, setBudgetYears] = useState([props.property.budgetYear]);
    const [property, setProperty] = useState(props.property);
    const data = usePropertyExecutiveSummaryData(property, props.user);
    const {data: narrativeData, loading: narrativeLoading, refetch: refetchNarrative} = useGetExecutiveSummaryNarrativeQuery({
        variables: {
            propertyId: property.id,
            budgetYear: property.budgetYear,
        },
        fetchPolicy: "no-cache",
        skip: !customNarrativeFeatureEnabled,
    });
    const [fetchBudgetApproval, {data: approvalData, loading: approvalDataLoading}] = useGetBudgetApprovalStatusLazyQuery({
        fetchPolicy: "no-cache"
    });
    // NOTE: Never fetch this for Acquisition Properties. It is meaningless.
    const [fetchBudgetYears, {data: budgetYearsData, loading: budgetYearsDataLoading}] = useListPropertyBudgetYearsLazyQuery({
        fetchPolicy: "no-cache"
    });
    const [setBudgetApproval] = useSetBudgetApprovalMutation({
        onCompleted: () => {
            fetchBudgetApproval({
                variables: {
                    propertyId: property.id,
                    budgetYear: property.budgetYear
                }
            });
        }
    });
    // NOTE: Never Fetch the Property again when the property is an Acquisition Property.
    const [loadPropertyForBudgetYear, {data: propertyForBudgetYear, loading: propertyForBudgetYearLoading}] = useGetMyPropertiesLazyQuery({
        fetchPolicy: "no-cache"
    });
    const [selectedGraphMetric, setSelectedGraphMetric] = useState<SERIES_DATA_OPTION>(SERIES_DATA_OPTION.OCCUPANCY);
    const appStore = useAppStore();
    const [selectedTab, setSelectedTab] = useState<ExecutiveSummaryTabs>(ExecutiveSummaryTabs.summary);
    const [isAtTop, setIsAtTop] = useState(true);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showGameModal, setShowGameModal] = useState(false);
    const {user: authUser} = useContext(AuthContext);
    const history = useHistory();
    const isEditingSummary = useRouteMatch(`/${ReportsNavMenuConfig.EDIT_EXECUTIVE_SUMMARY}`) !== null;

    const clientBranding = props.branding;

    const isRestrictionActive = isExecutiveSummaryAccessRestricted(authUser);
    const canShare = !props.user.isGuest && (!isRestrictionActive || checkAuthorization(authUser, Authorizations.ShareExecutiveSummary));
    const canApprove = props.user.isGuest || !isRestrictionActive || checkAuthorization(authUser, Authorizations.ShareExecutiveSummary);
    const narrative = narrativeData?.getExecutiveSummaryNarrative;
    let customNarrativeParagraphs: string[] = [];

    if (narrative?.useCustomNarrative) {
        customNarrativeParagraphs = narrative.sections.filter(section => section.body !== "").map(section => section.body);
    }

    useEffect(() => {
        appStore.set({isLoading: false});

        const isGuestUser = props.user.isGuest;

        const scrollEl: HTMLElement | null =
            document.getElementById("uw-content-container") ||
            document.getElementById("viz-app-content-wrapper") ||
            null;

        // guest user page doesn't live within a scrolling element, so we use the window scrollY
        function getScrollTop() {
            if (isGuestUser) {
                return window.scrollY;
            }

            return scrollEl?.scrollTop ?? 0;
        }

        function handleScroll() {
            // accounts for subpixel values, since I want to treat anything less than 1 whole pixel as top of the page
            setIsAtTop(getScrollTop() <= 1);
        }

        if (isGuestUser) {
            window.addEventListener("scroll", handleScroll, {passive: true});
        } else if (scrollEl) {
            scrollEl.addEventListener("scroll", handleScroll, {passive: true});
        }

        return () => {
            if (isGuestUser) {
                window.removeEventListener("scroll", handleScroll);
            } else if (scrollEl) {
                scrollEl.removeEventListener("scroll", handleScroll);
            }
        };
    }, []);

    useEffect(() => {
        if(props.property.isAcquisitionProperty) {
            // None of this is executable on an acquisition property.
            return;
        }

        if(property.budgetYear !== budgetYear) {
            loadPropertyForBudgetYear({
                variables: {propertyIds: [property.id], budgetYear}
            });
        }
        else if(property.id != props.property.id) {
            if(!props.user.isGuest) {
                // for guest user budget year is hard set with budget shared with the guest user
                // no other budget year can be viewed by the guest user
                fetchBudgetYears({
                    variables: {
                        propertyId: props.property.id
                    }
                });
            }
            setProperty(props.property);
            setBudgetYear(props.property.budgetYear);
        }
        else {
            // first time effect called on initial mount
            if(!props.user.isGuest) {
                // for guest user budget year is hard set with budget shared with the guest user
                // no other budget year can be viewed by the guest user
                fetchBudgetYears({
                    variables: {
                        propertyId: props.property.id
                    }
                });
            }
        }
    }, [budgetYear, props.property]);

    useEffect(() => {
        if(!propertyForBudgetYear || propertyForBudgetYearLoading) {
            return;
        }
        const loadedProperty: AuthUser["properties"][0] | undefined = propertyForBudgetYear.myProperties.find(p => p.id == property.id);
        if(!loadedProperty) {
            return;
        }
        setProperty(mapProperty(loadedProperty));
    }, [propertyForBudgetYear, propertyForBudgetYearLoading]);

    useEffect(() => {
        fetchBudgetApproval({
            variables: {
                propertyId: property.id,
                budgetYear: property.budgetYear
            }
        });
    }, [property.id, property.budgetYear]);

    useEffect(() => {
        if(!budgetYearsData || budgetYearsDataLoading) {
            return;
        }
        // List of all possible budget years is narrowed to show only meaningful for years following after
        // (after means years greater than) props.property.budgetYear.
        // props.property.budgetYear - points to currently active budget season/year
        //
        // We decided to explicitly set minimal cut off year to 2024 (NOTE: TI is the only client having data for 2023)
        // But in case someone decides to switch budget season to 2023 here is minYear logic

        let minYear = 2024;
        if(props.property.budgetYear < minYear) {
            minYear = props.property.budgetYear;
        }
        let budgetYears = [props.property.budgetYear, ...budgetYearsData.findMeaningfulClientBudgetPlanPropertyRecords.map(r => r.budgetYear)];
        budgetYears = budgetYears.filter(bYear => {
            if(bYear < minYear) {
                return false;
            }
            if(bYear <= props.property.budgetYear) {
                return true;
            }
            return budgetYearsData.findMeaningfulClientBudgetPlanPropertyRecords.find(record => record.budgetYear == bYear)?.meaningful ?? false;
        });
        budgetYears = budgetYears.dedupe().sort((a, b) => b - a);
        setBudgetYears(budgetYears);
    }, [budgetYearsData, budgetYearsDataLoading]);

    const chartData: ExecutiveSummaryGraphData | undefined = useMemo(
        () => {
            if(!data) {
                return undefined;
            }
            return data.graphSeriesData.get(selectedGraphMetric);
        },
        [data, selectedGraphMetric]
    );

    const customT12ChartData: ExecutiveSummaryGraphData | undefined = useMemo(() => {
        if(!data) {
            return undefined;
        }
        return data.customT12GraphSeriesData.get(selectedGraphMetric);
    }, [data, selectedGraphMetric]);

    const operationalMenuOptions = useMemo(() => {
        if(!data) {
            return [];
        }

        return OPERATIONAL_MENU_OPTIONS.filter(opt => data.graphSeriesData.has(opt.value));
    }, [data]);

    const budgetApprovalButtonOrLabel = useMemo(() => {
        if(!approvalData || approvalDataLoading) {
            return <div className={cn(css.approvalArea, css.approvalAreaBorder)}><Inline color={COLORS.PRIMARY_500} /></div>;
        }
        const approveButtonLabel = (approvalData?.getBudgetApprovalStatus?.approved)
            ? "Revoke Approval"
            : "Approve";
        if(!canApprove) {
            return (
                <div className={css.approvalArea}>
                    <Tooltip content="Please contact your admin to enable permission to approve this executive summary." size="large">
                        <Button style={{width: "100%"}} onClick={() => null}>
                            {approveButtonLabel}
                        </Button>
                    </Tooltip>
                </div>
            );
        }
        return (
            <div className={css.approvalArea}>
                <Button style={{width: "100%"}} isDanger={approvalData?.getBudgetApprovalStatus?.approved} onClick={() => handleBudgetApproval()}>
                    {approveButtonLabel}
                </Button>
            </div>
        );
    }, [approvalDataLoading, approvalData, canApprove]);

    const budgetYearsDropdown = useMemo(() => {
        if(budgetYearsDataLoading) {
            return <div className={cn(css.budgetYearsDropDownArea, css.budgetYearsDropDownAreaBorder)}><Inline color={COLORS.PRIMARY_500} /></div>;
        }
        if(props.user.isGuest || property.isAcquisitionProperty) {
            return (
                <span style={{fontSize: "0.8rem", lineHeight: "1.5rem"}}>
                    {propertyDisplayBudgetYear(property)}
                </span>
            );
        }
        return (
            <div className={css.budgetYearsDropDownArea}>
                <Dropdown
                    selectedItem={budgetYear}
                    onSelect={(v) => setBudgetYear(v)}
                >
                    <Field>
                        <Select disabled={budgetYears.length < 2 || isEditingSummary}>
                            {budgetYear}
                        </Select>
                    </Field>
                    <Menu>
                        {budgetYears.map((bYear) =>
                            <Item key={bYear} value={bYear}>
                                {bYear}
                            </Item>
                        ) ?? null}
                    </Menu>
                </Dropdown>
            </div>
        );
    }, [property, budgetYears, budgetYear, budgetYearsDataLoading, isEditingSummary]);

    const metricData = {
        mktRent: data?.avgMarketRentBudget ? {
            title: "avg budgeted market rents",
            isSmallTitle: true,
            values: [
                {
                    value: data && data.avgMarketRentBudget !== null ? formatterDollarUSNoDecimal.format(data.avgMarketRentBudget) : "",
                    bottomLabel: <div><span>{data && data.avgMarketRentDeltaPercent !== null ? formatterPercentWithSign.format(data.avgMarketRentDeltaPercent) : ""}</span> YOY</div>,
                }
            ],
            text: <div>{formatAsJSX(buildRentsStatementMarketRent(data))}</div>,
        } : null,
        occupancy: {
            title: "occupancy",
            isSmallTitle: true,
            values: [
                {
                    value: data && data.occupancyStat.budgetAvgOccupancy !== null ? formatterPercentAutoSign.format(data.occupancyStat.budgetAvgOccupancy) : "",
                    bottomLabel: property && !property.isAcquisitionProperty && data && data.occupancyStat.budgetAvgOccupancy !== null
                        && data.occupancyStat.reforecastDecember !== null
                        ?
                        `${formatterPercentWithSign.format(data.occupancyStat.budgetAvgOccupancy - data.occupancyStat.reforecastDecember)} vs '${data.property.reforecastYear % 100} Reforecast`
                        : "",
                }
            ],
            text: formatAsJSX(buildRentsStatementOccupancy(data, property.isAcquisitionProperty)),
        },
        resTurn: {
            title: "resident turnover",
            isSmallTitle: true,
            values: [
                {
                    topLabel: "Renewal Ratio",
                    value: buildRentsStatementRenewalRatio(data),
                },
                {
                    topLabel: "Move Out Ratio",
                    value: buildRentsStatementMoveOutRatio(data),
                }
            ],
            text: buildRentsStatementResidentsTurnover(data),
        },
        tradeouts: {
            title: "tradeouts",
            isSmallTitle: true,
            values: [
                {
                    topLabel: "New Lease Tradeout",
                    value: data && data.newLeaseTradeOutPercent !== null ?
                        formatterPercentWithSign.format(data.newLeaseTradeOutPercent)
                        :
                        "",
                },
                {
                    topLabel: "Renewal Tradeout",
                    value: data && data.renewalTradeOutPercent !== null ?
                        formatterPercentWithSign.format(data.renewalTradeOutPercent)
                        :
                        "",
                }
            ],
            text: buildRentsStatementTradeouts(data),
        }
    };

    function handleBudgetApproval() {
        setBudgetApproval({
            variables: {
                propertyId: property.id,
                budgetYear: property.budgetYear,
                approved: !(approvalData?.getBudgetApprovalStatus?.approved)
            }
        });
    }

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <div id="graph_tmp_anchor" style={{position: "absolute", visibility: "hidden", width: "500px", height: "300px"}}></div>
            <div className={cn(css.wrapper, (props.user.isGuest && css.isGuestUser), (isEditingSummary && css.isEditingSummary), clientBranding.stylingClass)}>
                <div className={css.header}>
                    <div className={css.headerActions}>
                        <ToggleButton
                            isBasic
                            isPressed={selectedTab == ExecutiveSummaryTabs.summary}
                            disabled={isEditingSummary}
                            onClick={() => setSelectedTab(ExecutiveSummaryTabs.summary)}
                        >
                            Analysis
                        </ToggleButton>
                        <ToggleButton
                            isBasic
                            isPressed={selectedTab == ExecutiveSummaryTabs.financials}
                            disabled={isEditingSummary}
                            onClick={() => setSelectedTab(ExecutiveSummaryTabs.financials)}
                        >
                            Financials
                        </ToggleButton>
                        <ToggleButton
                            isBasic
                            isPressed={selectedTab == ExecutiveSummaryTabs.revenue}
                            disabled={isEditingSummary}
                            onClick={() => setSelectedTab(ExecutiveSummaryTabs.revenue)}
                        >
                            Revenue
                        </ToggleButton>
                        <ToggleButton
                            isBasic
                            isPressed={selectedTab == ExecutiveSummaryTabs.payroll}
                            disabled={isEditingSummary}
                            onClick={() => setSelectedTab(ExecutiveSummaryTabs.payroll)}
                        >
                            Payroll
                        </ToggleButton>

                        <div style={
                            {
                                marginLeft: "auto",
                                display: "flex",
                                flexDirection: "row",
                                gap: "1rem",
                                alignItems: "center"
                            }
                        }>
                            <span style={{fontSize: "0.8rem", lineHeight: "1.5rem"}}>Budget Year</span>
                            <Tooltip
                                type="light"
                                zIndex={100}
                                content={props.user.isGuest ?
                                    "You see budget for the year specified" :
                                    budgetYears.length < 2 ?
                                        `Information for this property is available only for year of ${budgetYear}`
                                        : "Selecting Budget Year allows you to view your budgets created in Vizibly for different years"}
                            >
                                <InfoIcon />
                            </Tooltip>
                            {budgetYearsDropdown}
                            {
                                data?.graphSeriesData &&
                                <DownloadButton data={data} rawChartData={data.graphSeriesData} clientBranding={clientBranding} customNarrativeParagraphs={customNarrativeParagraphs} />
                            }
                            {canShare ?
                                <Button onClick={() => setShowShareModal(true)} style={{marginLeft: "auto"}}>
                                    Share
                                    <Button.EndIcon><ShareRounded /></Button.EndIcon>
                                </Button> :
                                <Tooltip content="Please contact your admin to enable permission to share this executive summary." size="large">
                                    <Button onClick={() => null} style={{marginLeft: "auto"}}>
                                        Share
                                        <Button.EndIcon><ShareRounded /></Button.EndIcon>
                                    </Button>
                                </Tooltip>
                            }
                            {budgetApprovalButtonOrLabel}
                        </div>
                    </div>
                    <div className={css.brandColorBand} style={{backgroundColor: clientBranding.colorSecondary}}>
                        {/* TODO:bowman this is a switch on our special tri-color asset living branding, but should be revisited after deadline */}
                        {ASSET_LIVING_WORKSPACE_IDS.includes(props.user.clientId) ?
                                <>
                                    <div className={cn(css.colorBar, css.colorBarBlue)}></div>
                                    <div className={cn(css.colorBar, css.colorBarGreen)}></div>
                                    <div className={cn(css.colorBar, css.colorBarOrange)}></div>
                                </>
                                : <></>
                        }
                    </div>

                    {selectedTab == ExecutiveSummaryTabs.summary && !isEditingSummary &&
                        <ExecutiveSummaryBanner
                            bannerImg={clientBranding.banner}
                            logoImg={clientBranding.logo}
                            clientName={data?.user.clientDisplayName}
                            propertyName={data?.property.name}
                            budgetYear={data?.property.budgetYear}
                            colorPrimary={clientBranding.colorPrimary}
                            isCollapsed={!isAtTop}
                            bannerImgPosition={clientBranding.bannerImagePosition}
                            logoSize={clientBranding.bannerLogoSize}
                        />
                    }
                </div>

                {isEditingSummary && customNarrativeFeatureEnabled ?
                    <NarrativeEditor
                        key={`${property.id}-${property.budgetYear}`}
                        property={property}
                        onDone={() => {
                            refetchNarrative();
                            history.push(`/${ReportsNavMenuConfig.PROPERTY_EXECUTIVE_SUMMARY}`);
                        }}
                    />
                    :
                    <Tabs selectedItem={selectedTab} onChange={setSelectedTab} style={{overflow: "visible"}}>
                        <TabPanel item={ExecutiveSummaryTabs.summary}>
                            <div className={css.summaryTabWrapper}>

                                <div className={css.summaryContent}>
                                    <section className={css.executiveSummary} id="executive-summary">
                                        <ExecutiveSummaryMetricBlock
                                            data={data}
                                            customNarrativeParagraphs={customNarrativeParagraphs}
                                            narrativeLoading={narrativeLoading}
                                            onEditSummary={props.user.isAdmin && customNarrativeFeatureEnabled ? () => history.push(`/${ReportsNavMenuConfig.EDIT_EXECUTIVE_SUMMARY}`) : undefined}
                                        />
                                    </section>

                                    <section className={css.rentSummary} id="rents">
                                        <MetricBlock
                                            title="Rent Summary"
                                            text={formatAsJSX(buildRentsStatementSummary(data))}
                                        />

                                        <div style={{display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "1rem"}}>
                                            {metricData.mktRent &&
                                                <MetricBlock
                                                    title={metricData.mktRent.title}
                                                    isSmallTitle={metricData.mktRent.isSmallTitle}
                                                    values={metricData.mktRent.values}
                                                    text={metricData.mktRent.text}
                                                />
                                            }
                                            <MetricBlock
                                                title={metricData.occupancy.title}
                                                isSmallTitle={metricData.occupancy.isSmallTitle}
                                                values={metricData.occupancy.values}
                                                text={metricData.occupancy.text}
                                            />
                                            <MetricBlock
                                                title={metricData.resTurn.title}
                                                isSmallTitle={metricData.resTurn.isSmallTitle}
                                                values={metricData.resTurn.values}
                                                text={metricData.resTurn.text}
                                            />
                                            <MetricBlock
                                                title={metricData.tradeouts.title}
                                                isSmallTitle={metricData.tradeouts.isSmallTitle}
                                                values={metricData.tradeouts.values}
                                                text={metricData.tradeouts.text}
                                            />
                                        </div>

                                        <div style={{marginBottom: "2rem"}}>
                                            {
                                                data ?
                                                    <GraphTableWidget>
                                                        <GraphTableWidget.Label>
                                                            <div className={widgetCss.widgetLabel}>
                                                                <MenuTitle
                                                                    options={operationalMenuOptions}
                                                                    onChange={(newVal) => setSelectedGraphMetric(newVal)}
                                                                    noUnderline
                                                                />
                                                            </div>
                                                            <div className={widgetCss.widgetSublabel}>
                                                                {!data.isUsingCustomT12 &&
                                                                    <>
                                                                        <MenuTitle
                                                                            options={[
                                                                                {
                                                                                    label: `${data.property.reforecastYear % 100} RFCST`,
                                                                                    value: 0,
                                                                                }
                                                                            ]}
                                                                            small
                                                                            plain
                                                                        />
                                                                        <span className={widgetCss.widgetSublabelMetricDivider}>vs</span>
                                                                    </>
                                                                }
                                                                <MenuTitle
                                                                    options={[
                                                                        {
                                                                            label: `${propertyDisplayBudgetYear(data.property) % 100} BDGT`,
                                                                            value: 0,
                                                                        }
                                                                    ]}
                                                                    small
                                                                    plain
                                                                />
                                                            </div>
                                                        </GraphTableWidget.Label>
                                                        <GraphTableWidget.Graph>
                                                            <Grid>
                                                                {!data.isUsingCustomT12 &&
                                                                    <Row justifyContent="center">
                                                                        <Col textAlign="center">
                                                                            <h5 className={statCSS.statLabel}>{chartData?.summary.comparisonItemOne.label ?? "-"}</h5>
                                                                            <h5 className={statCSS.statValue}>{chartData?.summary.comparisonItemOne.value ?? "-"}</h5>
                                                                        </Col>

                                                                        <Col textAlign="center">
                                                                            <h5 className={statCSS.statLabel}>{chartData?.summary.comparisonItemTwo.label ?? "-"}</h5>
                                                                            <h5 className={statCSS.statValue}>{chartData?.summary.comparisonItemTwo.value ?? "-"}</h5>
                                                                        </Col>

                                                                        <Col textAlign="center">
                                                                            <h5 className={statCSS.statLabel}>{chartData?.summary.variancePercent.label}</h5>
                                                                            <h5 className={statCSS.statValue}>{chartData?.summary.variancePercent.value}</h5>
                                                                        </Col>

                                                                        <Col textAlign="center">
                                                                            <h5 className={statCSS.statLabel}>{chartData?.summary.varianceAmount.label}</h5>
                                                                            <h5 className={statCSS.statValue}>{chartData?.summary.varianceAmount.value}</h5>
                                                                        </Col>
                                                                    </Row>
                                                                }

                                                                <Row>
                                                                    <Col>
                                                                        {chartData && customT12ChartData ?
                                                                            <OperationalGraph
                                                                                chartData={{
                                                                                    varianceCounts: !data.isUsingCustomT12 ?
                                                                                        chartData.varianceCounts :
                                                                                        customT12ChartData.varianceCounts,
                                                                                    chartSeriesData: !data.isUsingCustomT12 ?
                                                                                        chartData.chartSeriesData :
                                                                                        customT12ChartData.chartSeriesData
                                                                                }}
                                                                                dataType={chartData.dataType}
                                                                                months={getMonths(property)}
                                                                            />
                                                                            : <WidgetLoading />
                                                                        }
                                                                    </Col>
                                                                </Row>
                                                            </Grid>
                                                        </GraphTableWidget.Graph>
                                                        <GraphTableWidget.Table>
                                                            {
                                                                chartData?.tableData && customT12ChartData?.tableData ?
                                                                    <OperationalTable tableData={!data.isUsingCustomT12 ? chartData.tableData : customT12ChartData.tableData} />
                                                                    : <WidgetLoading />
                                                            }
                                                        </GraphTableWidget.Table>
                                                    </GraphTableWidget>
                                                    : <WidgetLoading />
                                            }
                                        </div>
                                    </section>

                                    <section className={css.financialSummary} id="financial-summary">
                                        <MetricBlock
                                            title="Financial Summary"
                                            text={formatAsJSX(buildExecutiveSummaryStatementNOI(data))}
                                        />

                                        {data &&
                                            <div>
                                                {!data.isUsingCustomT12 ?
                                                    data.reportTables.map((reportTable, i) =>
                                                        <div key={i} style={{marginBottom: "2rem"}}>
                                                            <FinancialReportTable
                                                                reforecastYearLabel={data.property.reforecastYear}
                                                                budgetYear={propertyDisplayBudgetYear(data.property)}
                                                                data={reportTable}
                                                            />
                                                        </div>
                                                    ) :
                                                    data.customT12ReportTables.map((reportTable, i) =>
                                                        <div key={i} style={{marginBottom: "2rem"}}>
                                                            <FinancialReportTable
                                                                reforecastYearLabel={CUSTOM_T12_TEXT}
                                                                budgetYear={propertyDisplayBudgetYear(data.property)}
                                                                data={reportTable}
                                                                customReforecastVsLabel={`${CUSTOM_T12_TEXT} (${data.customT12Label})`}
                                                            />
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        }
                                    </section>

                                </div>
                            </div>

                            <div className={css.footer}>
                                <div onClick={() => setShowGameModal(true)}>
                                    <img src={brand} alt="vizibly-brand" className={css.footerVizLogo} />
                                    {
                                        clientBranding.logoMini && <>
                                            <div className={css.footerPlusIcon}>
                                                <AddRounded className={css.footerIconAdd} />
                                                <Favorite className={css.footerIconHeart} />
                                            </div>
                                            <img src={`${clientBranding.logoMini}`} className={css.footerPartnerLogo} style={{width: `${clientBranding.footerLogoSize ?? 100}px`}} />
                                        </>
                                    }
                                </div>
                                {!!showGameModal && (
                                    <Modal onClose={() => setShowGameModal(false)} className={css.gameModal}>
                                        <Header>Need a little break from all the numbers?</Header>
                                        <Body>
                                            <FlappyViz />
                                        </Body>
                                        <Close aria-label="Close game modal" />
                                    </Modal>
                                )}
                            </div>
                        </TabPanel>

                        <TabPanel item={ExecutiveSummaryTabs.financials}>
                            <div style={{padding: "1rem 1rem 0 1rem"}}>
                                {
                                    data && !data.isUsingCustomT12 && data.rawData && data.financialsTable && !data.rawDataLoading &&
                                    approvalData && !approvalDataLoading &&
                                    <FinancialsCOATable
                                        key={property.id}
                                        data={data.financialsTable}
                                        commentsMap={data.commentsMap}
                                        property={property}
                                        readonlyComments={Boolean(approvalData.getBudgetApprovalStatus?.approved)}
                                        isIncome={data.isIncome}
                                    />
                                }
                                {
                                    data && data.isUsingCustomT12 && data.rawData && data.customT12FinancialsTable && !data.rawDataLoading &&
                                    approvalData && !approvalDataLoading &&
                                    <FinancialsCOATable
                                        key={property.id}
                                        data={data.customT12FinancialsTable}
                                        commentsMap={data.commentsMap}
                                        property={property}
                                        readonlyComments={Boolean(approvalData.getBudgetApprovalStatus?.approved)}
                                        customReforecastTotalHeader={`${CUSTOM_T12_TEXT} (${data.customT12Label})`}
                                        isIncome={data.isIncome}
                                    />
                                }
                            </div>
                        </TabPanel>

                        <TabPanel item={ExecutiveSummaryTabs.revenue}>
                            <div style={{padding: "1rem 1rem 0 1rem"}}>
                                {data && data.rawData && data.rawData.originalRevenueModel &&
                                    <RevenueSummaryTable
                                        unitTypeData={data.rawData.originalRevenueModel}
                                        property={property}
                                        unitTypes={data.unitTypes}
                                        showLabel
                                        hideReforecast={data.isUsingCustomT12}
                                    />
                                }
                            </div>
                        </TabPanel>

                        <TabPanel item={ExecutiveSummaryTabs.payroll}>
                            <div style={{padding: "1rem 1rem 0 1rem"}}>
                                {data && data.employeeTotals &&
                                    <EmployeeTable property={property} employeeTotals={data.employeeTotals} />
                                }
                            </div>
                        </TabPanel>
                    </Tabs>
                }
            </div>
            {showShareModal && data && <ShareModal property={data.property} onClose={() => setShowShareModal(false)} />}
        </ThemeProvider >
    );
}

export function PropertyExecutiveSummary(props: IPropertyExecutiveSummaryProps): ReactElement {
    const {data: brandingInformation, loading: brandingLoading} = useGetBrandingInformationQuery({
        fetchPolicy: "no-cache"
    });

    const clientBranding = useMemo(() => {
        let defaultBranding = loadClientBrandAsset(props.user.rootClientId || props.user.clientId);
        if(brandingInformation?.getBrandingInformationAllowNulls) {
            const branding = brandingInformation.getBrandingInformationAllowNulls;
            defaultBranding = {
                ...defaultBranding,
                colorPrimary: branding.primaryColor,
                colorSecondary: branding.secondaryColor,
                banner: branding.backgroundImage,
                logo: branding.logo,
                logoMini: branding.logoMini,
                bannerLogoSize: branding.bannerLogoSize,
                bannerImagePosition: branding.bannerImagePosition,
                footerLogoSize: branding.footerLogoSize,
                pdfImagePosition: branding.pdfImagePosition,
                pdfLogoSize: branding.pdfLogoSize,
                showSignOffPage: branding.showSignOffPage,
            };
        }
        return defaultBranding;
    }, [props.user, brandingInformation]);

    return <>
        {!brandingLoading && <BrandedPropertyExecutiveSummary {...props} branding={clientBranding} />}
    </>;
}
